import 'intersection-observer';
import React from 'react';
import { BreakpointQueryName } from '@doveit/lego';
import { NextPage } from 'next';
import InfoSection, { InfoSectionProps } from '../client/templates/common/info-section/InfoSection';
import HowItWorksSection, { HowItWorksSectionProps } from '../client/templates/common/how-it-works-section/HowItWorksSection';
import BottomBar, { BottomBarProps } from '../client/templates/common/bottom-bar/BottomBar';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import { requireFromStaticAsset } from '../client/utils/assetsUtils';
import scrollToRef from '../client/utils/scroll-to-ref/scrollToRef';
import SectionDivider from '../client/components/section-divider/SectionDivider';
import OurServicesSection, { OurServicesSectionProps } from '../client/templates/common/our-services-section/OurServicesSection';
import { buildSellPropertyURLObject } from '../client/utils/links';
import PageSection from '../client/components/page-section/PageSection';
import SavingCalculator from '../client/components/saving-calculator/SavingCalculator';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import CityList from '../client/components/city-list/CityList';
import {
  City, HeroSection, TextAlign,
} from '../client/types';
import CreateEvaluation from '../client/containers/create-evaluation/CreateEvaluation';
import LogosSection, { LogosSectionProps } from '../client/templates/common/logos-section/LogosSection';
import { getServices } from '../services/our-services/ourServices';
import { sendEvaluationFormSubmitEventAndRedirect } from '../client/utils/tracking';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import Hero from '../client/templates/common/hero/Hero';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import LeadInvitationPopup from '../client/components/lead-invitation-popup/LeadInvitationPopup';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { SELL_PROPERTIES } from '../client/graphql/queries/sellProperties';
import * as domain from '../domain/graphcms';

export interface SellPropertiesProps {
  SEO: PageHeadProps['SEO'],
  hero: HeroSection,
  infoSection: InfoSectionProps,
  citiesSection: {
    title: string,
    description: string,
    cities: City[],
  },
  howItWorksSection: HowItWorksSectionProps,
  channelsSection: LogosSectionProps,
  savingCalculatorSection: {
    title: string,
  },
  bottomBar: BottomBarProps,
  ourServicesSection: OurServicesSectionProps,
}

const INTERSECTION_OPTIONS = { threshold: 0 };

const SellProperties: NextPage<SellPropertiesProps> = ({
  SEO,
  hero,
  infoSection,
  citiesSection,
  howItWorksSection,
  channelsSection,
  ourServicesSection,
  savingCalculatorSection,
  bottomBar,
}) => {
  const { current, ref: formRef, isVisible: isFormVisible } = useRefInView(INTERSECTION_OPTIONS);

  return (
    <AppLayout activePage="sell-property">
      <PageHead SEO={SEO} />
      <LeadInvitationPopup
        trigger={{
          element: <Hero
            title={hero.title}
            description={hero.description}
            backgroundImage={hero.backgroundImage}
            secondarySlotTitle={hero.formTitle}
            secondarySlot={(
              <div ref={formRef}>
                <CreateEvaluation
                  onSubmit={() => sendEvaluationFormSubmitEventAndRedirect('hero:create-evaluation')}
                />
              </div>
            )}
          />,
          when: 'out-of-view',
        }}
      />

      <OurServicesSection {...ourServicesSection} />

      <SectionDivider />

      <InfoSection
        title={infoSection.title}
        reasons={infoSection.reasons}
      />

      <HowItWorksSection
        title={howItWorksSection.title}
        picture={howItWorksSection.picture}
        steps={howItWorksSection.steps}
      />

      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Title textAlign={TextAlign.CENTER}>
            {savingCalculatorSection.title}
          </PageSection.Title>
          <PageSection.Block>
            <SavingCalculator />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <SectionDivider />

      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Title textAlign={TextAlign.CENTER}>
            {citiesSection.title}
          </PageSection.Title>
          <PageSection.Description
            text={citiesSection.description}
            textAlign={TextAlign.CENTER}
          />
          <PageSection.Block>
            <CityList
              cities={citiesSection.cities.map((item) => ({
                name: item.title,
                picture: item.smallPicture,
                title: `Valutazione immobile a ${item.title}`,
                urlObject: buildSellPropertyURLObject(item.locality),
              }))}
            />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <SectionDivider />

      <LogosSection
        title={channelsSection.title}
        description={channelsSection.description}
        items={channelsSection.items}
      />

      <BottomBar
        expanded={!isFormVisible}
        hiddenFrom={BreakpointQueryName.MD}
        label={bottomBar.label}
        title={bottomBar.title}
        headline={bottomBar.headline}
        onClick={scrollToRef(current)}
      />
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<SellPropertiesProps>(async () => {
  const graphQLClient = initGraphQLClient();

  const [common, data, sellPropertyListings, services] = await Promise.all([
    importDynamicData('common.yml'),
    importDynamicData('pages/sell-property.yml'),
    graphQLClient.query<{ sellPropertyListings: domain.SellPropertyListing[] }>({
      query: SELL_PROPERTIES,
    }),
    getServices(),
  ]);

  const sellPropertyListing = sellPropertyListings.data.sellPropertyListings[0];

  return {
    props: {
      ...common,
      ...data,
      hero: {
        title: sellPropertyListing.hero.title.markdown,
        description: sellPropertyListing.hero.content.markdown,
        backgroundImage: {
          SMALL: sellPropertyListing.hero.mobileImage?.url || null,
          LARGE: sellPropertyListing.hero.desktopImage?.url || null,
        },
      },
      citiesSection: {
        title: sellPropertyListing.sellPropertiesSection.title.markdown,
        description: sellPropertyListing.sellPropertiesSection.content.markdown,
        cities: sellPropertyListing.sellPropertiesSection.sellProperties
          .sort((a, b) => a.locality.name.localeCompare(b.locality.name))
          .map((agency) => ({
            title: agency.locality.name,
            picture: agency.locality.coverPreviewVertical?.url || requireFromStaticAsset('cities/default-city.png'),
            smallPicture: agency.locality.coverPreviewHorizontal?.url || requireFromStaticAsset('cities/small/default-city.png'),
            locality: agency.locality.slug,
          })),
      },
      ourServicesSection: {
        ...common.ourServicesSection,
        services,
      },
    },
  };
});

export default withError(SellProperties);
